<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import { useToaster } from '@/composables/toaster';
import CInput from '@/components/shared/CInput';
import CSelect, { valueReducer } from '@/components/shared/CSelect';
import type { CanvasEx } from '@/typings/canvas';
import type {
    QrCornersDotType,
    QrCornersSquareType,
    QrDotsType,
    QrShape,
    QrStyleOptions,
    VectorPlaceholderElement
} from '@/typings/customilyElements/canvasElements';
import {
    DEFAULT_QR_STYLE_OPTIONS,
    QR_CORNERS_DOT_TYPES,
    QR_CORNERS_SQUARE_TYPES,
    QR_DOTS_TYPES,
    QR_SHAPES,
    isStyledQrConfig,
    updateStyledQrOptions
} from '@/utils/qrCodeUtils';

const props = defineProps<{
    canvas: CanvasEx;
    vectorElement?: VectorPlaceholderElement;
    editMode?: boolean;
}>();

// The service renders styled QRs on a 1000x1000 canvas and the margin is the px quiet zone
//  inside it, so anything below ~20 is barely visible.
const MIN_MARGIN = 0;
const MAX_MARGIN = 200;

const model = reactive({
    shape: DEFAULT_QR_STYLE_OPTIONS.shape as QrShape,
    margin: DEFAULT_QR_STYLE_OPTIONS.margin,
    dotsType: DEFAULT_QR_STYLE_OPTIONS.dotsOptions.type as QrDotsType,
    cornersSquareType: DEFAULT_QR_STYLE_OPTIONS.cornersSquareOptions.type as QrCornersSquareType,
    cornersDotType: DEFAULT_QR_STYLE_OPTIONS.cornersDotOptions.type as QrCornersDotType
});

watch(
    () => props.vectorElement,
    (element) => {
        if (!element || !isStyledQrConfig(element.qrConfig)) return;

        const options = element.qrConfig.options;
        model.shape = options.shape;
        model.margin = options.margin;
        model.dotsType = options.dotsOptions.type;
        model.cornersSquareType = options.cornersSquareOptions.type;
        model.cornersDotType = options.cornersDotOptions.type;
    },
    { immediate: true }
);

const disabled = computed(() => !!props.vectorElement?.locked);

const currentOptions = computed<QrStyleOptions | undefined>(() =>
    isStyledQrConfig(props.vectorElement?.qrConfig) ? props.vectorElement!.qrConfig.options : undefined
);

const toaster = useToaster();
const applyOptions = async (options: Partial<QrStyleOptions>) => {
    try {
        await updateStyledQrOptions(props.vectorElement, props.canvas, options, props.editMode);
    } catch (error) {
        console.error('Error updating QR code style:', error);
        toaster.error('An error occurred while generating the QR code');
    }
};

const onShapeSelected = () => applyOptions({ shape: model.shape });

const onMarginChanged = () => {
    model.margin = Math.max(MIN_MARGIN, Math.min(MAX_MARGIN, Number(model.margin) || 0));
    applyOptions({ margin: model.margin });
};

const onDotsTypeSelected = () => {
    const dotsOptions = currentOptions.value?.dotsOptions;
    if (!dotsOptions) return;
    applyOptions({ dotsOptions: { ...dotsOptions, type: model.dotsType } });
};

const onCornersSquareTypeSelected = () => {
    const cornersSquareOptions = currentOptions.value?.cornersSquareOptions;
    if (!cornersSquareOptions) return;
    applyOptions({ cornersSquareOptions: { ...cornersSquareOptions, type: model.cornersSquareType } });
};

const onCornersDotTypeSelected = () => {
    const cornersDotOptions = currentOptions.value?.cornersDotOptions;
    if (!cornersDotOptions) return;
    applyOptions({ cornersDotOptions: { ...cornersDotOptions, type: model.cornersDotType } });
};
</script>

<template>
    <div class="qr-code-style-wrapper">
        <div class="row">
            <c-select
                v-model="model.shape"
                class="col-6"
                label="Shape"
                :options="QR_SHAPES"
                :model-transform="valueReducer"
                :disabled="disabled"
                @input="onShapeSelected"
            />
            <div class="col-6 d-flex align-items-center">
                <c-input
                    v-model="model.margin"
                    type="number"
                    class="flex-fill"
                    label="Margin"
                    :min="MIN_MARGIN"
                    :max="MAX_MARGIN"
                    :disabled="disabled"
                    @blur="onMarginChanged"
                />
            </div>
            <c-select
                v-model="model.dotsType"
                class="col-12"
                label="Dots style"
                :options="QR_DOTS_TYPES"
                :model-transform="valueReducer"
                :disabled="disabled"
                @input="onDotsTypeSelected"
            />
            <c-select
                v-model="model.cornersSquareType"
                class="col-6"
                label="Corner square style"
                :options="QR_CORNERS_SQUARE_TYPES"
                :model-transform="valueReducer"
                :disabled="disabled"
                @input="onCornersSquareTypeSelected"
            />
            <c-select
                v-model="model.cornersDotType"
                class="col-6"
                label="Corner dot style"
                :options="QR_CORNERS_DOT_TYPES"
                :model-transform="valueReducer"
                :disabled="disabled"
                @input="onCornersDotTypeSelected"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
.qr-code-style-wrapper {
    .row {
        row-gap: 0.625rem;
    }
}
</style>
